import React, { useMemo, useState } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { GitBranch, Table2, X, type LucideIcon } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BaseModal } from '../../ui/BaseModal/BaseModal';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { WORLD_CUP_STANDINGS_MODAL } from '../../../src/worldcup/worldcup.constants';
// Same style factory as the WC standings modal, for the reason CompetitionScreenHeader reuses the
// WC header's: both shells are meant to be the same modal, so they must not drift apart by hand.
import { getWorldCupStandingsModalStyles } from '../../worldcup/WorldCupStandingsModal/WorldCupStandingsModal.styles';
import {
  COMPETITION_STANDINGS_MODAL_COPY,
  COMPETITION_STANDINGS_MODAL_TEST_ID,
  COMPETITION_STANDINGS_VIEWS,
  type CompetitionStandingsModalView as ModalView,
} from './competitionStandingsModal.constants';

export interface CompetitionStandingsModalViewProps {
  isVisible: boolean;
  onDismiss: () => void;
  /** The league-phase table. Omitted when the competition publishes none. */
  table?: React.ReactNode;
  /** The knockout bracket. Omitted when the competition's stage order has no knockout rounds. */
  bracket?: React.ReactNode;
  /** Which pane to open on. Falls back to whichever pane exists. */
  initialView?: ModalView;
  testID?: string;
}

const TAB_ICONS: Record<ModalView, LucideIcon> = {
  [COMPETITION_STANDINGS_VIEWS.TABLE]: Table2,
  [COMPETITION_STANDINGS_VIEWS.BRACKET]: GitBranch,
};

const TAB_LABELS: Record<ModalView, string> = {
  [COMPETITION_STANDINGS_VIEWS.TABLE]: COMPETITION_STANDINGS_MODAL_COPY.TABLE_TAB,
  [COMPETITION_STANDINGS_VIEWS.BRACKET]: COMPETITION_STANDINGS_MODAL_COPY.BRACKET_TAB,
};

/**
 * The modal shell: full-screen, a Table | Bracket toggle, and whichever pane is selected.
 *
 * Panes arrive as nodes rather than being built here, so this file holds no data path and the
 * story can drive it with fixtures. Panes are also **earned**, exactly as the tabs they replace
 * were (AC-5.1, AC-6.1): a competition that publishes no table gets no Table tab, and one whose
 * stage order has no knockout rounds gets no Bracket tab. With a single pane the toggle is
 * dropped altogether rather than rendered with one dead option.
 */
export const CompetitionStandingsModalView: React.FC<CompetitionStandingsModalViewProps> = ({
  isVisible,
  onDismiss,
  table,
  bracket,
  initialView,
  testID,
}) => {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(
    () => getWorldCupStandingsModalStyles(theme, { safeAreaTop: insets.top }),
    [theme, insets.top],
  );
  const showWebHover = Platform.OS === 'web';
  const modalTestId = testID ?? COMPETITION_STANDINGS_MODAL_TEST_ID;

  const tabs = useMemo(() => {
    const available: ModalView[] = [];
    if (table != null) available.push(COMPETITION_STANDINGS_VIEWS.TABLE);
    if (bracket != null) available.push(COMPETITION_STANDINGS_VIEWS.BRACKET);
    return available;
  }, [table, bracket]);

  const defaultView = initialView != null && tabs.includes(initialView) ? initialView : tabs[0];
  const [view, setView] = useState<ModalView | undefined>(defaultView);
  const activeView = view != null && tabs.includes(view) ? view : defaultView;

  if (tabs.length === 0) return null;

  const activePane = activeView === COMPETITION_STANDINGS_VIEWS.BRACKET ? bracket : table;

  return (
    <BaseModal
      isVisible={isVisible}
      onDismiss={onDismiss}
      dismissible={false}
      blurIntensity={WORLD_CUP_STANDINGS_MODAL.BASE_MODAL_BLUR_INTENSITY}
      contentAlignment="flex-start"
      contentPosition={{ top: 0, right: 0, bottom: 0, left: 0 }}
      contentContainerStyle={styles.fullScreen}
      testID={modalTestId}
      contentTestID={`${modalTestId}-content`}
    >
      <View style={styles.fullScreen}>
        <View style={styles.header}>
          <View style={styles.titleRow}>
            <Text style={styles.title}>{COMPETITION_STANDINGS_MODAL_COPY.TITLE}</Text>
            <Pressable
              style={({ pressed, hovered }) => [
                styles.closeButton,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.closeButtonPressed,
                hovered && showWebHover && styles.closeButtonHover,
              ]}
              onPress={onDismiss}
              accessibilityRole="button"
              accessibilityLabel={COMPETITION_STANDINGS_MODAL_COPY.ACCESSIBILITY_CLOSE}
              hitSlop={12}
              testID={`${modalTestId}-close`}
            >
              <X size={20} color={theme.gray[600]} strokeWidth={2.2} />
            </Pressable>
          </View>

          {tabs.length > 1 ? (
            <View style={styles.tabBar}>
              <View style={styles.tabRow}>
                {tabs.map((id) => {
                  const Icon = TAB_ICONS[id];
                  const active = activeView === id;
                  return (
                    <Pressable
                      key={id}
                      style={({ pressed, hovered }) => [
                        styles.tabButton,
                        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                        pressed && styles.tabButtonPressed,
                        hovered && showWebHover && !active && styles.tabButtonHoverInactive,
                      ]}
                      onPress={() => setView(id)}
                      accessibilityRole="tab"
                      accessibilityState={{ selected: active }}
                      testID={`${modalTestId}-tab-${id}`}
                    >
                      <Icon
                        size={16}
                        color={active ? theme.primary : theme.gray[500]}
                        strokeWidth={2.2}
                      />
                      <Text
                        style={[
                          styles.tabLabel,
                          active ? styles.tabLabelActive : styles.tabLabelInactive,
                        ]}
                      >
                        {TAB_LABELS[id]}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
              <View style={styles.tabUnderlineTrack}>
                <View
                  style={[
                    styles.tabUnderline,
                    activeView === COMPETITION_STANDINGS_VIEWS.BRACKET && styles.tabUnderlineRight,
                  ]}
                />
              </View>
            </View>
          ) : null}
        </View>

        <View style={styles.fullScreen} testID={`${modalTestId}-pane-${activeView}`}>
          {activePane}
        </View>
      </View>
    </BaseModal>
  );
};
